import React from 'react';
import {
    ApprovalInstance,
    TimeRecordRequestApprovalInstance,
} from '../../../api/ApprovalApi';
import {TimeRecordEntrySnapshot} from '../../../api/RequestApi';
import {BaseApprovalDetailModal} from './BaseApprovalDetailModal';
import {DetailRow, DetailSection} from './BaseApprovalDetailComponents';
import Typography from '../../../components/Typography';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import {ArrowRightIcon} from "../../../components/Icons";


const ENTRY_TYPE_STYLES: Record<string, string> = {
    WORK: 'bg-blue-50 text-blue-700',
    BREAK: 'bg-gray-50 text-gray-600',
    ABSENCE: 'bg-amber-50 text-amber-700',
};

const ENTRY_TYPE_LABELS: Record<string, string> = {
    WORK: 'Arbeit',
    BREAK: 'Pause',
    ABSENCE: 'Abwesenheit',
};

function hhmmToMinutes(hhmm: string | null | undefined): number | null {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
}

function formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
}

function entryDuration(entry: TimeRecordEntrySnapshot): string {
    if (entry.durationMinutes != null) return formatDuration(entry.durationMinutes);
    const start = hhmmToMinutes(entry.startTimestamp);
    const end = hhmmToMinutes(entry.endTimestamp);
    if (start == null) return '–';
    if (end == null) return 'Offen';
    const diff = end - start;
    return diff > 0 ? formatDuration(diff) : '–';
}

function totalWorkMinutes(entries: TimeRecordEntrySnapshot[]): number {
    return entries
        // .filter(e => e.entryType === 'WORK')
        .reduce((sum, e) => {
            if (e.durationMinutes != null) return sum + e.durationMinutes;
            const start = hhmmToMinutes(e.startTimestamp);
            const end = hhmmToMinutes(e.endTimestamp);
            return start != null && end != null ? sum + (end - start) : sum;
        }, 0);
}

function EntryTable({entries, emptyLabel}: { entries: TimeRecordEntrySnapshot[]; emptyLabel?: string }) {
    if (!entries?.length) {
        return <p className="text-sm text-gray-400 py-3 text-center">{emptyLabel ?? 'Keine Einträge.'}</p>;
    }

    return (
        <table className="w-full text-sm">
            <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                <th className="pb-1 pr-3 font-medium">Typ</th>
                <th className="pb-1 pr-3 font-medium">Von</th>
                <th className="pb-1 pr-3 font-medium">Bis</th>
                <th className="pb-1 font-medium text-right">Dauer</th>
            </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
            {entries.map((entry, i) => (
                <tr key={i}>
                    <td className="py-1.5 pr-3">
                        <span className={clsx(
                            'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
                            ENTRY_TYPE_STYLES[entry.entryType] ?? ENTRY_TYPE_STYLES.WORK,
                        )}>
                            {ENTRY_TYPE_LABELS[entry.entryType] ?? entry.entryType}
                        </span>
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">{entry.startTimestamp ?? '–'}</td>
                    <td className="py-1.5 pr-3 tabular-nums">
                        {entry.endTimestamp ?? <span className="text-gray-400 italic">Offen</span>}
                    </td>
                    <td className="py-1.5 tabular-nums text-right text-gray-600">
                        {entryDuration(entry)}
                    </td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

function SummaryStrip({label, entries}: { label: string, entries: TimeRecordEntrySnapshot[] }) {
    const work = totalWorkMinutes(entries);
    // if (work === 0) return null;
    return (
        <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
            <Typography variant="caption" color="secondary">{label}</Typography>
            <Typography variant="caption" className="font-semibold text-gray-800">
                {formatDuration(work)}
            </Typography>
        </div>
    );
}

function groupByType(entries: TimeRecordEntrySnapshot[]) {
    return {
        work: entries.filter(e => e.entryType === "WORK"),
        break: entries.filter(e => e.entryType === "BREAK"),
        absence: entries.filter(e => e.entryType === "ABSENCE"),
    };
}

export interface TimeRecordApprovalDetailModalProps {
    approvalInstance: TimeRecordRequestApprovalInstance;
    open: boolean;
    onClose: () => void;
    onSuccess: (updated: ApprovalInstance) => void;
}

export function TimeRecordApprovalDetailModal({
                                                  approvalInstance,
                                                  open,
                                                  onClose,
                                                  onSuccess,
                                              }: TimeRecordApprovalDetailModalProps) {
    const {t} = useTranslation();
    const {approvableEntity} = approvalInstance;

    const originalEntries = approvableEntity.previousEntries ?? [];
    const originalEntriesGrouped = groupByType(originalEntries);

    const newEntries = approvableEntity.entries ?? [];
    const newEntriesGrouped = groupByType(approvableEntity.entries ?? []);

    const recordDate = approvableEntity.recordDate
        ? new Date(approvableEntity.recordDate).toLocaleDateString('de-DE', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        })
        : '–';

    // ── Left: original (before correction) ──────────────────────────────────

    // const leftContent = (
    //     <>
    //         <DetailSection title={t('timeRecordRequest.detail.record', {defaultValue: 'Datum'})}>
    //             <DetailRow label={t('timeRecordRequest.recordDate', {defaultValue: 'Datum'})}>
    //                 {recordDate}
    //             </DetailRow>
    //
    //             {approvableEntity.baseRevision != null && (
    //                 <DetailRow label="Revision">
    //                     {approvableEntity.baseRevision}
    //                 </DetailRow>
    //             )}
    //
    //             {approvableEntity.actualMinutes != null && (
    //                 <DetailRow label={t('timeRecordRequest.actualMinutes', {defaultValue: 'Netto'})}>
    //                     {formatDuration(approvableEntity.actualMinutes)}
    //                 </DetailRow>
    //             )}
    //
    //             {approvableEntity.breakMinutes != null && (
    //                 <DetailRow label={t('timeRecordRequest.breakMinutes', {defaultValue: 'Pause'})}>
    //                     {formatDuration(approvableEntity.breakMinutes)}
    //                 </DetailRow>
    //             )}
    //         </DetailSection>
    //
    //         <div className="rounded-md border border-gray-200 px-3 py-2">
    //             <EntryTable
    //                 entries={originalEntriesGrouped.work}
    //                 emptyLabel="Vorherige Einträge nicht verfügbar."
    //             />
    //             <SummaryStrip entries={originalEntriesGrouped.work}/>
    //             <SummaryStrip entries={originalEntriesGrouped.break}/>
    //         </div>
    //
    //         {approvableEntity.notes && (
    //             <DetailSection title={t('timeRecordRequest.detail.notes', {defaultValue: 'Notizen'})}>
    //                 <p className="py-2 text-sm text-gray-700 whitespace-pre-wrap">
    //                     {approvableEntity.notes}
    //                 </p>
    //             </DetailSection>
    //         )}
    //     </>
    // );

    // ── Right: new (requested) entries ───────────────────────────────────────

    // const rightContent = (
    //     <div className="rounded-md border border-blue-100 px-3 py-2 bg-blue-50/30">
    //         <EntryTable entries={newEntriesGrouped.work}/>
    //         <SummaryStrip entries={newEntriesGrouped.work}/>
    //         <SummaryStrip entries={newEntriesGrouped.break}/>
    //     </div>
    // );

    const instanceDetails = (
        <DetailSection title={t('timeRecordRequest.detail.record', {defaultValue: 'Zeiterfassung'})}>
            <DetailRow label={t('timeRecordRequest.recordDate', {defaultValue: 'Arbeitsdatum'})}>
                {recordDate}
            </DetailRow>

            {/*{approvableEntity.baseRevision != null && (*/}
            {/*    <DetailRow label="Revision">*/}
            {/*        {approvableEntity.baseRevision}*/}
            {/*    </DetailRow>*/}
            {/*)}*/}

            {approvableEntity.actualMinutes != null && (
                <DetailRow label={t('timeRecordRequest.actualMinutes', {defaultValue: 'Netto'})}>
                    {formatDuration(approvableEntity.actualMinutes)}
                </DetailRow>
            )}

            {approvableEntity.breakMinutes != null && (
                <DetailRow label={t('timeRecordRequest.breakMinutes', {defaultValue: 'Pause'})}>
                    {formatDuration(approvableEntity.breakMinutes)}
                </DetailRow>
            )}
        </DetailSection>
    );

    return (
        <BaseApprovalDetailModal
            instance={approvalInstance}
            open={open}
            onClose={onClose}
            onSuccess={onSuccess}
            title={t('timeRecordRequest.detail.title', {defaultValue: 'Zeitkorrektur'})}
            employee={approvableEntity.employee}
            instanceDetails={instanceDetails}
        >
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="rounded-md border border-gray-200 px-3 py-2">
                    <Typography variant={'overline'} color={'secondary'}
                                className="block mb-3 text-xs font-semibold tracking-wider uppercase">
                        {t('timeRecordRequest.detail.newWorkEntries', {defaultValue: 'Aktuelle Zeiten'})}
                    </Typography>
                    <EntryTable
                        entries={originalEntries}
                        emptyLabel="Vorherige Einträge nicht verfügbar."
                    />
                    <SummaryStrip label={'Pausenzeit'} entries={originalEntriesGrouped.break}/>
                    <SummaryStrip label={'Arbeitszeit'} entries={originalEntriesGrouped.work}/>
                </div>

                <div className="flex justify-center items-center">
                    <ArrowRightIcon className="w-5 h-5 text-gray-400"/>
                </div>

                <div className="rounded-md border border-blue-100 px-3 py-2 bg-blue-50/30">
                    <Typography variant={'overline'} color={'secondary'}
                                className="block mb-3 text-xs font-semibold tracking-wider uppercase">
                        {t('timeRecordRequest.detail.newWorkEntries', {defaultValue: 'Beantragte Änderungen'})}
                    </Typography>
                    <EntryTable entries={newEntries}/>
                    <SummaryStrip label={'Pausenzeit'} entries={newEntriesGrouped.break}/>
                    <SummaryStrip label={'Arbeitszeit'} entries={newEntriesGrouped.work}/>
                </div>
            </div>
        </BaseApprovalDetailModal>
    );
}